<template>
  <div class="saved-filters-view">
    <div class="container">
      <span class="back-to-playground" @click="backClicked">
        <arrow-svg class="arrow-icon"></arrow-svg>
        Back
      </span>
      <h1 class="page-title">
        My filter setups
        <tooltip
          :content="
            'Please note, storing your data is offered as a service. <br />We are not responsible for data loss of any kind, like - but not limited to - server crash, tool’s functionality update or addition, phase-out of older products.'
          "
        />
      </h1>

      <table class="simple-table" v-if="sortedFilterData.length">
        <thead>
          <th
            @click="changeSort('region')"
            ref="regionColumn"
            :style="getStyle('region')"
          >
            Region
            <up-svg
              class="up-icon"
              v-if="getSort('region') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="getSort('region') === 'asc'"
            ></down-svg>
          </th>
          <th
            @click="changeSort('description')"
            ref="descriptionColumn"
            :style="getStyle('description')"
          >
            Description
            <up-svg
              class="up-icon"
              v-if="getSort('description') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="getSort('description') === 'asc'"
            ></down-svg>
          </th>
          <th
            @click="changeSort('createdOn')"
            ref="createdOnColumn"
            :style="getStyle('createdOn')"
          >
            Date created
            <up-svg
              class="up-icon"
              v-if="getSort('createdOn') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="getSort('createdOn') === 'asc'"
            ></down-svg>
          </th>
          <th></th>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in sortedFilterData"
            :key="index"
            @mouseover="hover = row.name"
            @mouseleave="hover = ''"
            @click="onApplyFilter(row)"
            :class="{ disabled: row.region !== region }"
          >
            <td>
              {{ row.region }}
            </td>
            <td>
              {{ row.name }}
            </td>
            <td>{{ formatDate(row.createdOn, "yyyy-MM-dd") || "-" }}</td>
            <td style="text-align: center; width: 50px;">
              <transition name="slide-fade" mode="out-in">
                <a
                  href="#"
                  class="link-active link"
                  v-if="hover === row.name"
                  @click.stop="onDeleteClicked(row)"
                  ><delete-svg class="delete-icon"></delete-svg
                ></a>
              </transition>
            </td>
          </tr>
        </tbody>
      </table>

      <p class="meta-info" v-if="!sortedFilterData.length && !loading">
        No solutions have been saved yet.
      </p>
      <p class="meta-info" v-if="loading">Loading...</p>
    </div>
  </div>
</template>

<script>
import { http } from "@/services/http";
import { EventBus } from "@/eventbus.js";
import { format } from "date-fns";
import DeleteFiltersetModal from "@/components/modals/DeleteFiltersetModal";
import deleteSvg from "@/assets/svg/icons/signify-icons_16-delete-16px.svg?inline";
import arrowSvg from "@/assets/svg/icons/signify-icons_13-arrow-button-16px.svg?inline";
import downSvg from "@/assets/svg/icons/signify-icons_04-down-12px.svg?inline";
import upSvg from "@/assets/svg/icons/signify-icons_11-up-12px.svg?inline";
import { mapState } from "vuex";
import { cloneDeep } from "lodash";

export default {
  name: "SavedFiltersView",
  modals: { DeleteFiltersetModal },
  data() {
    return {
      filterData: [],
      hover: "",
      loading: true,
      sorting: null,
      columnWidths: null
    };
  },
  components: {
    deleteSvg,
    arrowSvg,
    downSvg,
    upSvg
  },
  computed: {
    ...mapState("brain", ["region"]),
    sortedFilterData() {
      if (!this.filterData) return [];
      if (!this.sorting) return this.filterData;

      const ascending = cloneDeep(this.filterData).sort((a, b) => {
        return a[this.sorting.property] > b[this.sorting.property] ? 1 : -1;
      });

      return this.sorting.order === "asc" ? ascending : ascending.reverse();
    }
  },
  watch: {
    sortedFilterData() {
      this.$nextTick(() => {
        this.columnWidths = {
          region: this.$refs.regionColumn.clientWidth,
          description: this.$refs.descriptionColumn.clientWidth,
          createdOn: this.$refs.createdOnColumn.clientWidth
        };
      });
    }
  },
  created() {
    this.$store.commit("brain/ui/showControls", false);
  },
  mounted() {
    this.getFilters();
    EventBus.$on("filtersSaved", () => {
      this.getFilters();
    });
  },
  methods: {
    formatDate(value) {
      return format(value, "yyyy-MM-dd");
    },
    onDeleteClicked(row) {
      this.$modalRouter.push({
        name: "DeleteFiltersetModal",
        props: { show: true, filterset: row },
        model: "show",
        on: { onDeleteFilters: this.deleteFilter }
      });
    },
    getFilters() {
      http.get(`/api/v2/users/profile/settings/filters/all`).then(response => {
        const filterData = response.data.data.filter(
          item => item.name !== "AUTOSAVE"
        ); //don't show the filter we reserved for autosave functionality
        this.filterData = this.sortFiltersByRegion(filterData);
        this.loading = false;
      });
    },

    deleteFilter(filter) {
      http({
        method: "delete",
        url: `/api/v2/users/profile/settings/filters/remove?name=${filter.name}`
      }).then(() => {
        this.getFilters();
      });
    },
    onApplyFilter(row) {
      if (row === null) return;

      const decodedFilters = JSON.parse(row.filters);
      function onComplete() {
        this.$store.dispatch("brain/solutions/handleNewSolution", false);
        this.$store.commit(
          "brain/global/setFilter",
          decodedFilters.globalFilters
        );
        this.$store.commit(
          "brain/modules/setFilters",
          decodedFilters.modulesFilters
        );
        this.$store.commit(
          "brain/drivers/setFilters",
          decodedFilters.driversFilters
        );
      }
      this.$router.push(
        { name: "advanced", params: { fetchFiltersOnInit: false } },
        onComplete.bind(this)
      );
    },
    sortFiltersByRegion(filters) {
      const regionOrderedFilters = filters
        .filter(filter => {
          return filter.region === this.region;
        })
        .sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        });

      filters = filters
        .filter(filter => {
          return filter.region !== this.region;
        })
        .sort((a, b) => {
          if (a.filter.region > b.filter.region) return 1;
          if (a.filter.region < b.filter.region) return -1;

          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
        });

      filters.map(filter => {
        regionOrderedFilters.push(filter);
      });

      return regionOrderedFilters;
    },
    backClicked() {
      this.$store.commit("brain/global/setCheckReload", true);
      this.$router.push({ name: "advanced" });
    },
    changeSort(property) {
      if (!this.sorting || this.sorting.property !== property) {
        this.sorting = {
          property: property,
          order: "desc"
        };
      } else {
        this.sorting.order = this.sorting.order === "desc" ? "asc" : "desc";
      }
    },
    getSort(property) {
      if (this.sorting && this.sorting.property === property) {
        return this.sorting.order;
      } else {
        return null;
      }
    },
    getStyle(property) {
      return this.columnWidths
        ? { width: `${this.columnWidths[property]}px` }
        : {};
    }
  },
  destroyed() {
    EventBus.$off("filtersSaved");
  }
};
</script>

<style lang="scss" scoped>
.page-title {
  margin-bottom: 40px;
}

.back-to-playground {
  margin-top: 115px;
  margin-bottom: 5px;
  display: block;
  color: $blue-500;
  cursor: pointer;
}

thead {
  border-top: 1px $mid-gray-200 solid;
  background: #fff;

  th {
    padding: 16px 10px;
  }
}

tbody {
  background: #fff;
}

th {
  cursor: pointer;
  min-width: 100px;
}

tr {
  cursor: pointer;

  &.disabled {
    cursor: default;
    opacity: 0.5;
  }
}

.delete-icon {
  width: 20px;

  path {
    fill: $dark-gray-900;
  }
}

.arrow-icon {
  width: 16px;
  transform: rotate(180deg);

  path {
    fill: $blue-500;
  }
}

.up-icon,
.down-icon {
  width: 16px;
}

.saved-filters-view {
  flex-grow: 2;
}
.filters-table {
  width: 100%;
  height: 400px;
}

.apply-filters {
  margin: 20px 0;
}
</style>
